// File Location: src/services/policyWriterService.js

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  onSnapshot,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebaseConfig";
import { createNotification, sendEmail } from './notificationService';
import { sendPolicyApprovalPushNotification } from './pushNotificationService';
import { updateRoleBucket } from './bucketFilterService';

// Get policy writer dashboard statistics
export const getPolicyWriterStats = async (policyWriterId) => {
  try {
    // Get pending applications for policy verification
    const pendingQuery = query(
      collection(db, "patientApplications"),
      where("workflow.policyVerification.status", "==", "pending"),
      where("status", "in", ["medical_approved", "policy_review_pending"])
    );
    const pendingSnapshot = await getDocs(pendingQuery);

    // Get applications reviewed by this policy writer
    const reviewedQuery = query(
      collection(db, "patientApplications"),
      where("workflow.policyVerification.verifiedBy", "==", policyWriterId)
    );
    const reviewedSnapshot = await getDocs(reviewedQuery);

    // Get today's reviews
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTimestamp = Timestamp.fromDate(today);

    let reviewedToday = 0;
    let totalReviewTime = 0;
    let approvedCount = 0;
    let rejectedCount = 0;
    let pendingInfoCount = 0;
    let emergencyPending = 0;
    let totalCoverageReviewed = 0;
    let totalClaimAmount = 0;

    // Count emergency cases in pending
    pendingSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.emergency) {
        emergencyPending++;
      }
    });

    // Calculate stats from reviewed applications
    reviewedSnapshot.forEach((doc) => {
      const data = doc.data();
      const reviewedAt = data.workflow?.policyVerification?.verifiedAt;

      if (reviewedAt && reviewedAt >= todayTimestamp) {
        reviewedToday++;
      }

      if (data.workflow?.policyVerification?.reviewTimeMinutes) {
        totalReviewTime += data.workflow.policyVerification.reviewTimeMinutes;
      }

      const status = data.workflow?.policyVerification?.status;
      if (status === "approved") approvedCount++;
      else if (status === "rejected") rejectedCount++;
      else if (status === "pending_info") pendingInfoCount++;

    // Calculate coverage and claim amounts
      totalCoverageReviewed += data.policy?.coverageAmount || 0;
      totalClaimAmount += data.medical?.estimatedCost || 0;
    });

    // Get counselling statistics
    let pendingCounselling = 0;
    let kypInitialPending = 0;
    let dischargePending = 0;

    // Check for applications needing counselling
    const allAppsQuery = query(
      collection(db, "patientApplications"),
      where("status", "in", ["approved", "policy_verified"])
    );
    const allAppsSnapshot = await getDocs(allAppsQuery);

    allAppsSnapshot.forEach((doc) => {
      const data = doc.data();
      const workflow = data.workflow?.policyVerification;
      
      // Check if KYP Initial Counselling is needed
      if (workflow?.kypInitialCounselling?.status === 'pending' || 
          (workflow?.status === 'approved' && !workflow?.kypInitialCounselling)) {
        kypInitialPending++;
        pendingCounselling++;
      }
      
      // Check if Discharge Counselling is needed  
      if (workflow?.dischargeCounselling?.status === 'pending' ||
          (data.status === 'approved' && !workflow?.dischargeCounselling)) {
        dischargePending++;
        pendingCounselling++;
      }
    });

    const averageReviewTime = reviewedSnapshot.size > 0
      ? Math.round(totalReviewTime / reviewedSnapshot.size)
      : 0;

    const approvalRate = reviewedSnapshot.size > 0
      ? Math.round((approvedCount / reviewedSnapshot.size) * 100)
      : 0;

    return {
      success: true,
      stats: {
        pendingReview: pendingSnapshot.size,
        reviewedToday,
        totalReviewed: reviewedSnapshot.size,
        averageReviewTime,
        emergencyPending,
        approvalRate,
        approvedCount,
        rejectedCount,
        pendingInfoCount,
        totalCoverageReviewed,
        totalClaimAmount,
        averageClaimRatio: totalCoverageReviewed > 0 
          ? Math.round((totalClaimAmount / totalCoverageReviewed) * 100)
          : 0,
        pendingCounselling,
        kypInitialPending,
        dischargePending
      }
    };
  } catch (error) {
    console.error("Error getting policy writer stats:", error);
    return {
      success: false,
      error: error.message,
      stats: {
        pendingReview: 0,
        reviewedToday: 0,
        totalReviewed: 0,
        averageReviewTime: 0,
        emergencyPending: 0,
        approvalRate: 0,
        approvedCount: 0,
        rejectedCount: 0,
        pendingInfoCount: 0,
        totalCoverageReviewed: 0,
        totalClaimAmount: 0,
        averageClaimRatio: 0,
        pendingCounselling: 0,
        kypInitialPending: 0,
        dischargePending: 0
      }
    };
  }
};

// Get applications pending policy verification
export const getPolicyWriterApplications = async (filters = {}, page = 1, pageSize = 10) => {
  try {
    let q = collection(db, "patientApplications");

    // Base filter - applications in or approaching policy verification
    // EXCLUDE dispute_pending to implement hold mechanism for unresolved queries
    // Include early pipeline statuses so policy can pre-triage new applications
    q = query(
      q,
      where("workflow.policyVerification.status", "==", "pending"),
      where("status", "in", [
        "medical_approved",
        "policy_review_pending",
        "approved_for_process",
        "submitted",
        "under_medical_review",
        "medical_review_pending",
        "under_review"
      ])
    );

    // Apply status filter
    if (filters.status && filters.status !== "") {
      q = query(q, where("status", "==", filters.status));
    }

    // Apply priority filter
    if (filters.priority && filters.priority !== "") {
      q = query(q, where("priority", "==", filters.priority));
    }

    // Apply emergency filter
    if (filters.emergency !== "" && filters.emergency !== undefined) {
      const isEmergency = filters.emergency === "true";
      q = query(q, where("emergency", "==", isEmergency));
    }

    // Apply treatment type filter
    if (filters.treatmentType && filters.treatmentType !== "") {
      q = query(q, where("medical.treatmentType", "==", filters.treatmentType));
    }

    // Apply insurance company filter
    if (filters.insuranceCompany && filters.insuranceCompany !== "") {
      q = query(q, where("policy.insuranceCompany", "==", filters.insuranceCompany));
    }

    // Apply hospital filter
    if (filters.hospitalName && filters.hospitalName !== "") {
      q = query(q, where("hospitalName", "==", filters.hospitalName));
    }

    // Order by priority and submission date (emergency first)
    q = query(q, orderBy("emergency", "desc"), orderBy("submittedAt", "desc"));

    const querySnapshot = await getDocs(q);
    let applications = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      applications.push({
        id: doc.id,
        ...data
      });
    });

    // Client-side filtering for search term
    if (filters.searchTerm && filters.searchTerm.trim() !== "") {
      const searchTerm = filters.searchTerm.toLowerCase();
      applications = applications.filter(app =>
        app.applicationId?.toLowerCase().includes(searchTerm) ||
        app.patient?.name?.toLowerCase().includes(searchTerm) ||
        app.policy?.number?.toLowerCase().includes(searchTerm) ||
        app.policy?.insuranceCompany?.toLowerCase().includes(searchTerm) ||
        app.hospitalName?.toLowerCase().includes(searchTerm)
      );
    }

    // Client-side date filtering
    if (filters.dateFrom && filters.dateFrom !== "") {
      const fromDate = new Date(filters.dateFrom);
      applications = applications.filter(app => {
        const submittedDate = app.submittedAt?.toDate ? app.submittedAt.toDate() : new Date(app.submittedAt);
        return submittedDate >= fromDate;
      });
    }

    if (filters.dateTo && filters.dateTo !== "") {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      applications = applications.filter(app => {
        const submittedDate = app.submittedAt?.toDate ? app.submittedAt.toDate() : new Date(app.submittedAt);
        return submittedDate <= toDate;
      });
    }

    // Apply amount range filters
    if (filters.minCoverage && filters.minCoverage !== "") {
      const minAmount = parseFloat(filters.minCoverage);
      applications = applications.filter(app =>
        (app.policy?.coverageAmount || 0) >= minAmount
      );
    }

    if (filters.maxCoverage && filters.maxCoverage !== "") {
      const maxAmount = parseFloat(filters.maxCoverage);
      applications = applications.filter(app =>
        (app.policy?.coverageAmount || 0) <= maxAmount
      );
    }

    // Pagination
    const total = applications.length;
    const startIndex = (page - 1) * pageSize;
    const paginatedApplications = applications.slice(startIndex, startIndex + pageSize);

    return {
      success: true,
      applications: paginatedApplications,
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize)
    };
  } catch (error) {
    console.error("Error getting policy writer applications:", error);
    return {
      success: false,
      error: error.message,
      applications: [],
      total: 0
    };
  }
};

// Get applications reviewed by specific policy writer
export const getPolicyWriterReviewedApplications = async (policyWriterId, filters = {}, page = 1, pageSize = 10) => {
  try {
    let q = collection(db, "patientApplications");

    // Base filter - only applications reviewed by this policy writer
    q = query(q, where("workflow.policyVerification.verifiedBy", "==", policyWriterId));

    // Apply status filter if provided
    if (filters.status && filters.status !== "") {
      q = query(q, where("workflow.policyVerification.status", "==", filters.status));
    }

    // Order by review date (newest first)
    q = query(q, orderBy("workflow.policyVerification.verifiedAt", "desc"));

    const querySnapshot = await getDocs(q);
    let applications = [];

    querySnapshot.forEach((doc) => {
      applications.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Client-side filtering for search term
    if (filters.searchTerm && filters.searchTerm.trim() !== "") {
      const searchTerm = filters.searchTerm.toLowerCase();
      applications = applications.filter(app =>
        app.applicationId?.toLowerCase().includes(searchTerm) ||
        app.patient?.name?.toLowerCase().includes(searchTerm) ||
        app.policy?.number?.toLowerCase().includes(searchTerm) ||
        app.policy?.insuranceCompany?.toLowerCase().includes(searchTerm)
      );
    }

    // Date range filtering for review date
    if (filters.dateFrom && filters.dateFrom !== "") {
      const fromDate = new Date(filters.dateFrom);
      applications = applications.filter(app => {
        const reviewDate = app.workflow?.policyVerification?.verifiedAt?.toDate();
        return reviewDate && reviewDate >= fromDate;
      });
    }

    if (filters.dateTo && filters.dateTo !== "") {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      applications = applications.filter(app => {
        const reviewDate = app.workflow?.policyVerification?.verifiedAt?.toDate();
        return reviewDate && reviewDate <= toDate;
      });
    }

    // Pagination
    const total = applications.length;
    const startIndex = (page - 1) * pageSize;
    const paginatedApplications = applications.slice(startIndex, startIndex + pageSize);

    return {
      success: true,
      applications: paginatedApplications,
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize)
    };
  } catch (error) {
    console.error("Error getting reviewed applications:", error);
    return {
      success: false,
      error: error.message,
      applications: [],
      total: 0
    };
  }
};

// Get single application for policy verification
export const getApplicationForPolicyReview = async (applicationId) => {
  try {
    const q = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return { success: false, error: "Application not found" };
    }

    const applicationDoc = querySnapshot.docs[0];
    const application = {
      id: applicationDoc.id,
      ...applicationDoc.data()
    };

    return {
      success: true,
      application
    };
  } catch (error) {
    console.error("Error getting application:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Complete policy verification
export const completePolicyVerification = async (applicationId, policyWriterId, verificationData) => {
  try {
    // Check for unresolved queries before allowing approval
    const queryCheck = await canApplicationProceedToPolicy(applicationId);
    if (!queryCheck.canProceed) {
      return {
        success: false,
        error: `Cannot complete policy verification: ${queryCheck.reason}`,
        queryInfo: {
          hasUnresolvedQueries: queryCheck.reason.includes("unresolved queries"),
          unresolvedQueries: queryCheck.unresolvedQueries || [],
          queryCount: queryCheck.queryCount || 0
        }
      };
    }

    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (appSnapshot.empty) {
      return { success: false, error: "Application not found" };
    }

    const appDoc = appSnapshot.docs[0];
  const appData = appDoc.data();
    const appRef = doc(db, "patientApplications", appDoc.id);

    // Calculate review time
    const reviewStartTime = verificationData.reviewStartTime || new Date();
    const reviewEndTime = new Date();
    const reviewTimeMinutes = Math.round((reviewEndTime - reviewStartTime) / (1000 * 60));

    // Update application with policy verification results
    const updateData = {
      'workflow.policyVerification.status': verificationData.status, // approved, rejected, pending_info
      'workflow.policyVerification.verifiedBy': policyWriterId,
      'workflow.policyVerification.verifiedAt': new Date(),
      'workflow.policyVerification.comments': verificationData.comments || '',
      'workflow.policyVerification.reviewTimeMinutes': reviewTimeMinutes,
      'workflow.policyVerification.policyAnalysis': verificationData.policyAnalysis || {},
      'workflow.policyVerification.approvedCoverageAmount': verificationData.approvedCoverageAmount || 0,
      'workflow.policyVerification.coverageBreakdown': verificationData.coverageBreakdown || {
        baseCoverage: 0,
        cumulativeBonus: 0,
        topUpCoverage: 0,
        totalAvailable: 0,
        utilized: 0,
        balance: 0,
      },
      'workflow.policyVerification.eligibilityConfirmed': verificationData.eligibilityConfirmed || false,
      'workflow.policyVerification.exclusionsApplied': verificationData.exclusionsApplied || [],
      // Store the complete verification data for KYP generation
      'workflow.policyVerification.policyDetails': verificationData.policyDetails || {},
      'workflow.policyVerification.roomEligibility': verificationData.roomEligibility || {},
      'workflow.policyVerification.coPayStructure': verificationData.coPayStructure || {},
      'workflow.policyVerification.deductiblesAndSublimits': verificationData.deductiblesAndSublimits || {},
      'workflow.policyVerification.claimHistory': verificationData.claimHistory || {},
      'workflow.policyVerification.intimationDetails': verificationData.intimationDetails || {},
      'workflow.policyVerification.dispatchAddress': verificationData.dispatchAddress || {},
      'workflow.policyVerification.clarificationDetails': verificationData.clarificationDetails || {},
      updatedAt: new Date()
    };

    // Update main status based on verification result
    if (verificationData.status === "approved") {
      updateData.status = "policy_approved";
      // Move to KYC verification stage
      updateData['workflow.kycVerification.status'] = "pending";
    } else if (verificationData.status === "rejected") {
      updateData.status = "policy_rejected";
    } else if (verificationData.status === "pending_info") {
      updateData.status = "pending_policy_clarification";
    }

    // If KYP was challenged and reassigned to Policy, mark progress and notify doctor to revise KYP
    try {
      const kypAckStatus = appData?.workflow?.medicalVerification?.kypHospitalAcknowledgement?.status;
      const reassignment = appData?.workflow?.medicalVerification?.kypReassignment;
      const targetsArr = reassignment?.targets || (reassignment?.target ? [reassignment.target] : []);
      const includesPolicy = targetsArr.includes('policy') || reassignment?.target === 'policy' || reassignment?.target === 'both';

      if (kypAckStatus === 'challenged' && includesPolicy) {
        // Mark policy side as completed for reassignment tracking
        updateData['workflow.medicalVerification.kypReassignment.policyCompletedAt'] = new Date();
        if (targetsArr.includes('finance')) {
          updateData['workflow.medicalVerification.kypReassignment.status'] = 'policy_completed';
        } else {
          updateData['workflow.medicalVerification.kypReassignment.status'] = 'completed';
        }

        // Notify the doctor (or medical department fallback)
        const doctorId = appData?.workflow?.medicalVerification?.verifiedBy || 'medical';
        const patientName = appData?.patient?.name || 'Patient';
        const appIdStr = appData?.applicationId || applicationId;
        await createNotification({
          recipientId: doctorId,
          recipientType: doctorId === 'medical' ? 'department' : 'user',
          type: 'application',
          subType: 'kyp_revision_requested',
          title: 'Policy verification updated for KYP challenge',
          message: `Policy team has updated verification for challenged KYP of ${patientName} (ID: ${appIdStr}). Please review and revise the KYP if needed.`,
          applicationId: appIdStr,
          actionUrl: `/doctor/application-review/${appIdStr}`,
          priority: 'high',
          metadata: {
            patientName,
            reassignmentTargets: targetsArr,
            source: 'policy'
          }
        });
      }
    } catch (notifyErr) {
      console.warn('Policy completion notification failed:', notifyErr);
    }

    await updateDoc(appRef, updateData);

    // Update bucket filters
    if (verificationData.status === "approved") {
      await updateRoleBucket(appDoc.id, 'policy', 'approved');
      await updateRoleBucket(appDoc.id, 'doctor', 'kyp_pending'); // Ready for KYP generation
    } else if (verificationData.status === "rejected") {
      await updateRoleBucket(appDoc.id, 'policy', 'rejected');
      await updateRoleBucket(appDoc.id, 'doctor', 'rejected');
    }

    // Cross-approval email notification: If Policy just approved and Finance already approved, notify Doctors team
    try {
      if ((verificationData.status || '').toLowerCase() === 'approved') {
        const finApproved = (
          (appData?.workflow?.financialVerification?.status || '').toLowerCase() === 'approved' ||
          (appData?.status || '').toLowerCase() === 'financial_approved'
        );
        if (finApproved && typeof sendEmail === 'function') {
          const patientName = appData?.patient?.name || 'Patient';
          const appIdStr = appData?.applicationId || applicationId;
          const subject = `Application ready for KYP – ${patientName} (ID: ${appIdStr})`;
          const html = `<!DOCTYPE html><html><body style="font-family:Arial,Helvetica,sans-serif;line-height:1.6;color:#0f172a;">
            <h2 style="margin:0 0 12px;color:#065f46;">Application Ready for KYP</h2>
            <p style="margin:0 0 8px;">The application <strong>${appIdStr}</strong> for <strong>${patientName}</strong> now has <strong>both Policy and Finance approvals</strong>.</p>
            <p style="margin:0 0 12px;"><strong>This application is ready for KYP process — please generate KYP.</strong></p>
            <p style="margin:0 0 8px;"><a href="${typeof window === 'undefined' ? '' : ''}/doctor/application-review/${appIdStr}" style="background:#2563eb;color:#fff;padding:8px 12px;border-radius:6px;text-decoration:none;display:inline-block;">Open to Generate KYP</a></p>
            <p style="margin:12px 0 0;font-size:12px;color:#64748b;">This is an automated notification.</p>
          </body></html>`;
          await sendEmail({ to: 'doctors@samhealth.co.in', subject, html });

          // Send push notification
          await sendPolicyApprovalPushNotification(appIdStr, patientName);
        }
      }
    } catch (emailErr) {
      console.warn('Cross-approval doctor email (policy) failed:', emailErr?.message || emailErr);
    }

    return {
      success: true,
      message: "Policy verification completed successfully"
    };
  } catch (error) {
    console.error("Error completing policy verification:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Save or update policy verification draft without changing approval status
export const savePolicyVerificationDraft = async (applicationId, policyWriterId, draftData = {}) => {
  try {
    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);
    if (appSnapshot.empty) {
      return { success: false, error: "Application not found" };
    }
    const appDoc = appSnapshot.docs[0];
    const appRef = doc(db, "patientApplications", appDoc.id);

    const updatePayload = {
      'workflow.policyVerification.verificationData': draftData || {},
      'workflow.policyVerification.draftBy': policyWriterId || null,
      'workflow.policyVerification.lastDraftAt': new Date(),
      updatedAt: new Date()
    };

    await updateDoc(appRef, updatePayload);
    return { success: true, updatedAt: updatePayload.lastDraftAt };
  } catch (error) {
    console.error('savePolicyVerificationDraft error:', error);
    return { success: false, error: error?.message || 'Failed to save draft' };
  }
};

// Create policy clarification request
export const createPolicyClarificationRequest = async (applicationId, policyWriterId, clarificationData) => {
  try {
    const requestData = {
      applicationId,
      requestedBy: policyWriterId,
      requestedAt: new Date(),
      status: "pending",
      type: "policy_clarification",
      subject: clarificationData.subject || "Policy Clarification Required",
      message: clarificationData.message,
      requestedDocuments: clarificationData.requestedDocuments || [],
      priority: clarificationData.priority || "normal"
    };

    // Add to clarifications collection
    const clarificationRef = await addDoc(collection(db, "clarifications"), requestData);

    // Update application status
    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (!appSnapshot.empty) {
      const appRef = doc(db, "patientApplications", appSnapshot.docs[0].id);
      await updateDoc(appRef, {
        status: "pending_policy_clarification",
        'workflow.policyVerification.status': "pending_info",
        'workflow.policyVerification.clarificationRequestId': clarificationRef.id,
        updatedAt: new Date()
      });
    }

    return {
      success: true,
      clarificationId: clarificationRef.id,
      message: "Policy clarification request sent successfully"
    };
  } catch (error) {
    console.error("Error creating policy clarification request:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Upload policy verification document
export const uploadPolicyVerificationDocument = async (applicationId, file, documentType, uploadedBy) => {
  try {
    const fileName = `policy_${documentType}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${file.name}`;
    const storageRef = ref(storage, `policy-verification/${applicationId}/${fileName}`);

    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    const documentData = {
      url: downloadURL,
      fileName: file.name,
      originalName: file.name,
      size: file.size,
      type: documentType,
      uploadedAt: new Date(),
      uploadedBy: uploadedBy,
      applicationId: applicationId
    };

    // Save document record
    const docRef = await addDoc(collection(db, "policyVerificationDocuments"), documentData);

    return {
      success: true,
      document: { ...documentData, id: docRef.id }
    };
  } catch (error) {
    console.error("Error uploading policy verification document:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get policy writer performance metrics
export const getPolicyWriterPerformanceMetrics = async (policyWriterId, dateRange = null) => {
  try {
    let q = collection(db, "patientApplications");
    q = query(q, where("workflow.policyVerification.verifiedBy", "==", policyWriterId));

    if (dateRange && dateRange.start && dateRange.end) {
      q = query(
        q,
        where("workflow.policyVerification.verifiedAt", ">=", Timestamp.fromDate(new Date(dateRange.start))),
        where("workflow.policyVerification.verifiedAt", "<=", Timestamp.fromDate(new Date(dateRange.end)))
      );
    }

    const querySnapshot = await getDocs(q);

    const metrics = {
      totalReviewed: 0,
      approved: 0,
      rejected: 0,
      pendingInfo: 0,
      averageReviewTime: 0,
      totalReviewTime: 0,
      emergencyReviews: 0,
      complexCases: 0,
      approvalRate: 0,
      totalCoverageReviewed: 0,
      totalClaimAmount: 0,
      avgDailyReviews: 0,
      coverageUtilizationRate: 0
    };

    let totalReviewTime = 0;
    let totalCoverage = 0;
    let totalClaims = 0;
    const reviewDates = new Set();

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      metrics.totalReviewed++;

      const reviewStatus = data.workflow?.policyVerification?.status;
      switch (reviewStatus) {
        case "approved":
          metrics.approved++;
          break;
        case "rejected":
          metrics.rejected++;
          break;
        case "pending_info":
          metrics.pendingInfo++;
          break;
      }

      if (data.emergency) {
        metrics.emergencyReviews++;
      }

      if (data.priority === "urgent" || data.policy?.coverageAmount > 500000) {
        metrics.complexCases++;
      }

      const reviewTime = data.workflow?.policyVerification?.reviewTimeMinutes;
      if (reviewTime) {
        totalReviewTime += reviewTime;
      }

      const coverage = data.policy?.coverageAmount || 0;
      const claimAmount = data.medical?.estimatedCost || 0;
      totalCoverage += coverage;
      totalClaims += claimAmount;

      // Track unique review dates
      const reviewDate = data.workflow?.policyVerification?.verifiedAt?.toDate();
      if (reviewDate) {
        reviewDates.add(reviewDate.toDateString());
      }
    });

    metrics.averageReviewTime = metrics.totalReviewed > 0
      ? Math.round(totalReviewTime / metrics.totalReviewed)
      : 0;
    metrics.totalReviewTime = totalReviewTime;
    metrics.totalCoverageReviewed = totalCoverage;
    metrics.totalClaimAmount = totalClaims;

    // Calculate approval rate
    metrics.approvalRate = metrics.totalReviewed > 0
      ? Math.round((metrics.approved / metrics.totalReviewed) * 100)
      : 0;

    // Calculate coverage utilization rate
    metrics.coverageUtilizationRate = totalCoverage > 0
      ? Math.round((totalClaims / totalCoverage) * 100)
      : 0;

    // Calculate average daily reviews
    metrics.avgDailyReviews = reviewDates.size > 0
      ? Math.round(metrics.totalReviewed / reviewDates.size)
      : 0;

    return {
      success: true,
      metrics
    };
  } catch (error) {
    console.error("Error getting policy writer performance metrics:", error);
    return {
      success: false,
      error: error.message,
      metrics: null
    };
  }
};

// Real-time listener for application updates during policy review
export const subscribeToApplicationUpdates = (applicationId, callback) => {
  const q = query(
    collection(db, "patientApplications"),
    where("applicationId", "==", applicationId)
  );

  return onSnapshot(q, (snapshot) => {
    if (!snapshot.empty) {
      const application = {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data()
      };
      callback(application);
    }
  });
};

// Get list of insurance companies for filter
export const getInsuranceCompaniesForFilter = async () => {
  try {
    // Get unique insurance companies from applications
    const q = query(collection(db, "patientApplications"));
    const querySnapshot = await getDocs(q);
    const companies = new Set();

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.policy?.insuranceCompany) {
        companies.add(data.policy.insuranceCompany);
      }
    });

    return {
      success: true,
      companies: Array.from(companies).sort()
    };
  } catch (error) {
    console.error("Error getting insurance companies:", error);
    return {
      success: false,
      error: error.message,
      companies: [
        "HDFC ERGO", "ICICI Lombard", "Bajaj Allianz", "Star Health", "Care Health",
        "Niva Bupa", "Aditya Birla", "Future Generali", "Cholamandalam", "Oriental Insurance"
      ]
    };
  }
};

// Get hospitals for filter
export const getHospitalsForFilter = async () => {
  try {
    const q = query(
      collection(db, "hospitals"),
      where("status", "==", "approved")
    );

    const querySnapshot = await getDocs(q);
    const hospitals = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      hospitals.push({
        id: doc.id,
        name: data.hospitalName || data.name,
        city: data.address?.city || ""
      });
    });

    return {
      success: true,
      hospitals
    };
  } catch (error) {
    console.error("Error getting hospitals:", error);
    return {
      success: false,
      error: error.message,
      hospitals: []
    };
  }
};

// CONSULTATION SCHEDULING SYSTEM

// Schedule consultation with patient
export const schedulePatientConsultation = async (applicationId, policyWriterId, consultationData) => {
  try {
    const consultationRef = await addDoc(collection(db, "policyConsultations"), {
      applicationId,
      policyWriterId,
      consultationType: consultationData.type, // 'kyp-initial-counselling' or 'discharge-counselling'
      scheduledDate: Timestamp.fromDate(new Date(consultationData.scheduledDate)),
      scheduledTime: consultationData.scheduledTime,
      contactMethod: consultationData.contactMethod, // 'phone', 'video', 'in-person'
      patientPhone: consultationData.patientPhone,
      patientEmail: consultationData.patientEmail,
      agenda: consultationData.agenda || [],
      status: 'scheduled',
      createdAt: Timestamp.now(),
      createdBy: policyWriterId,
      reminderSent: false,
      notes: consultationData.notes || '',
      priority: consultationData.priority || 'normal' // normal, urgent, emergency
    });

    // Update application workflow
    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (!appSnapshot.empty) {
      const appRef = doc(db, "patientApplications", appSnapshot.docs[0].id);
      const updatePath = consultationData.type === 'kyp-initial-counselling' 
        ? 'workflow.policyVerification.kypInitialCounselling'
        : 'workflow.policyVerification.dischargeCounselling';
      
      await updateDoc(appRef, {
        [`${updatePath}.status`]: 'scheduled',
        [`${updatePath}.consultationId`]: consultationRef.id,
        [`${updatePath}.scheduledAt`]: Timestamp.fromDate(new Date(consultationData.scheduledDate)),
        [`${updatePath}.scheduledBy`]: policyWriterId,
        updatedAt: Timestamp.now()
      });
    }

    return {
      success: true,
      consultationId: consultationRef.id,
      message: `${consultationData.type} scheduled successfully`
    };
  } catch (error) {
    console.error("Error scheduling consultation:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Complete consultation with discussion details
export const completePatientConsultation = async (consultationId, completionData) => {
  try {
    const consultationRef = doc(db, "policyConsultations", consultationId);
    const consultationDoc = await getDoc(consultationRef);
    
    if (!consultationDoc.exists()) {
      return { success: false, error: "Consultation not found" };
    }

    const consultation = consultationDoc.data();

    // Upload audio recording if provided
    let audioRecordingUrl = null;
    if (completionData.audioFile) {
      const audioFileName = `consultation_${consultationId}_${Date.now()}.mp3`;
      const audioRef = ref(storage, `policy-consultations/recordings/${audioFileName}`);
      const audioSnapshot = await uploadBytes(audioRef, completionData.audioFile);
      audioRecordingUrl = await getDownloadURL(audioSnapshot.ref);
    }

    // Update consultation record
    await updateDoc(consultationRef, {
      status: 'completed',
      completedAt: Timestamp.now(),
      completedBy: completionData.completedBy,
      duration: completionData.duration, // in minutes
      topicsDiscussed: completionData.topicsDiscussed || [],
      audioRecordingUrl: audioRecordingUrl,
      consultationNotes: completionData.consultationNotes || '',
      patientFeedback: completionData.patientFeedback || '',
      actionItems: completionData.actionItems || [],
      followUpRequired: completionData.followUpRequired || false,
      followUpDate: completionData.followUpDate ? Timestamp.fromDate(new Date(completionData.followUpDate)) : null,
      patientSatisfaction: completionData.patientSatisfaction || null, // 1-5 rating
      issues: completionData.issues || [],
      resolutions: completionData.resolutions || [],
      updatedAt: Timestamp.now()
    });

    // Update application workflow
    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", consultation.applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (!appSnapshot.empty) {
      const appRef = doc(db, "patientApplications", appSnapshot.docs[0].id);
      const updatePath = consultation.consultationType === 'kyp-initial-counselling' 
        ? 'workflow.policyVerification.kypInitialCounselling'
        : 'workflow.policyVerification.dischargeCounselling';
      
      await updateDoc(appRef, {
        [`${updatePath}.status`]: 'completed',
        [`${updatePath}.completedAt`]: Timestamp.now(),
        [`${updatePath}.summary`]: completionData.consultationNotes,
        [`${updatePath}.audioRecordingUrl`]: audioRecordingUrl,
        [`${updatePath}.followUpRequired`]: completionData.followUpRequired,
        updatedAt: Timestamp.now()
      });
    }

    return {
      success: true,
      message: "Consultation completed successfully",
      audioRecordingUrl
    };
  } catch (error) {
    console.error("Error completing consultation:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get consultation scripts for policy discussions
export const getConsultationScripts = (consultationType, applicationData) => {
  const scripts = {
    'kyp-initial-counselling': {
      title: "KYP Initial Counselling Script - Policy Verification & Clarification",
      introduction: "Hello {patientName}, this is {reviewerName} from {companyName}. I'm calling regarding your health insurance application {applicationId}. Do you have a few minutes to discuss your policy details?",
      sections: [
        {
          title: "Policy Verification",
          points: [
            "Let me verify your policy details with you:",
            "Policy Number: {policyNumber}",
            "Insurance Company: {insuranceCompany}",
            "Sum Insured: ₹{sumInsured}",
            "Policy Period: {policyPeriod}",
            "Are these details correct?"
          ]
        },
        {
          title: "Treatment Details Confirmation",
          points: [
            "I need to confirm the treatment details:",
            "Diagnosis: {diagnosis}",
            "Treating Hospital: {hospitalName}",
            "Estimated Treatment Cost: ₹{estimatedCost}",
            "Admission Date: {admissionDate}",
            "Is this information accurate?"
          ]
        },
        {
          title: "Policy Coverage Discussion",
          points: [
            "Let me explain your policy coverage:",
            "Your current available coverage is ₹{availableCoverage}",
            "Room eligibility: {roomEligibility}",
            "Co-payment applicable: {coPayment}",
            "Any pre-existing condition waiting period: {waitingPeriod}"
          ]
        },
        {
          title: "Documentation Requirements",
          points: [
            "For your claim processing, we need:",
            "✓ Medical records and discharge summary",
            "✓ Original bills and receipts",
            "✓ Investigation reports",
            "✓ Policy document copy",
            "Do you have all these documents ready?"
          ]
        },
        {
          title: "Next Steps",
          points: [
            "Your application is under review",
            "We'll complete the verification within 24-48 hours",
            "You'll receive updates via SMS and email",
            "Is there anything else you'd like to clarify about your policy?"
          ]
        }
      ],
      conclusion: "Thank you for your time, {patientName}. If you have any questions, please call our helpline. We'll keep you updated on your application status."
    },
    'discharge-counselling': {
      title: "Discharge Counselling Script - Approval/Clarification Follow-up",
      introduction: "Hello {patientName}, this is {reviewerName} from {companyName}. I'm calling to update you on your health insurance application {applicationId} and discuss the next steps.",
      sections: [
        {
          title: "Application Status Update",
          points: [
            "Your application has been reviewed",
            "Status: {applicationStatus}",
            "Approved Coverage: ₹{approvedCoverage}",
            "Effective Date: {effectiveDate}"
          ]
        },
        {
          title: "Coverage Details (If Approved)",
          points: [
            "Your approved benefits include:",
            "Room Rent: {roomRentLimit} per day",
            "ICU Coverage: {icuCoverage}",
            "Pre & Post Hospitalization: {prePostCoverage}",
            "Co-payment: {finalCoPayment}"
          ]
        },
        {
          title: "Required Actions (If Any)",
          points: [
            "To proceed with your treatment:",
            "Submit any pending documents",
            "Coordinate with hospital for cashless facility",
            "Maintain all original bills and receipts",
            "Follow the treatment plan as advised"
          ]
        },
        {
          title: "Important Reminders",
          points: [
            "Keep your policy document handy",
            "Inform us of any changes in treatment plan",
            "Contact us immediately for any emergencies",
            "Use only network hospitals for cashless claims"
          ]
        }
      ],
      conclusion: "Is there anything else I can help clarify? We're here to support you throughout your treatment. Wishing you a speedy recovery!"
    }
  };

  // Replace placeholders with actual data
  const replaceTemplate = (text, data) => {
    return text.replace(/\{(\w+)\}/g, (match, key) => {
      return (data?.[key] !== undefined && data?.[key] !== null && data?.[key] !== '') ? data[key] : match;
    });
  };

  // Helpers for safe formatting across varied data shapes
  const toDate = (val) => {
    try {
      if (!val) return null;
      if (typeof val?.toDate === 'function') return val.toDate();
      if (typeof val === 'number') return new Date(val);
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    } catch (_) {
      return null;
    }
  };

  const formatDate = (val) => {
    const d = toDate(val);
    return d ? d.toLocaleDateString('en-IN', { year: 'numeric', month: '2-digit', day: '2-digit' }) : null;
  };

  const formatCurrency = (num) => {
    const n = Number(num);
    if (!isFinite(n)) return null;
    return n.toLocaleString('en-IN');
  };

  // Pull from multiple possible locations with graceful fallbacks
  const policy = applicationData?.policy || {};
  const pv = applicationData?.workflow?.policyVerification || {};
  const policyDetails = pv?.policyDetails || {};
  const roomEligibility = pv?.roomEligibility || {};
  const coPayStructure = pv?.coPayStructure || {};
  const coverageBreakdown = pv?.coverageBreakdown || {};
  const medical = applicationData?.medical || {};

  const sumInsuredRaw = policy.coverageAmount ?? policy.sumInsured ?? policyDetails.sumInsured ?? coverageBreakdown.totalAvailable;
  const sumInsured = formatCurrency(sumInsuredRaw);

  const availableCoverageRaw = coverageBreakdown.balance ?? pv.approvedCoverageAmount ?? policy.coverageAmount ?? policy.sumInsured;
  const availableCoverage = formatCurrency(availableCoverageRaw);

  const policyNumber = policy.number || policy.policyNumber || policyDetails.policyNumber;
  const insuranceCompany = policy.insuranceCompany || policy.company || policyDetails.insuranceCompany;

  // Policy period
  const start = policy.startDate || policyDetails.startDate || policyDetails.policyStart || policyDetails.policyPeriod?.start;
  const end = policy.endDate || policyDetails.endDate || policyDetails.policyEnd || policyDetails.policyPeriod?.end;
  const startStr = formatDate(start) || (typeof start === 'string' ? start : null);
  const endStr = formatDate(end) || (typeof end === 'string' ? end : null);
  const policyPeriod = (startStr && endStr) ? `${startStr} to ${endStr}` : `${startStr || '[Start]'} to ${endStr || '[End]'}`;

  const diagnosis = medical.diagnosis || medical.primaryDiagnosis || applicationData?.diagnosis || '[Diagnosis]';
  const hospitalName = applicationData?.hospitalName || applicationData?.hospital?.name || '[Hospital Name]';
  const estimatedCost = formatCurrency(medical.estimatedCost ?? applicationData?.financial?.estimatedAmount) || '[Treatment Cost]';

  const admissionDateRaw = medical.admissionDate || applicationData?.admission?.date;
  const admissionDate = formatDate(admissionDateRaw) || admissionDateRaw || '[Admission Date]';

  const roomEligibilityText = roomEligibility?.roomType || roomEligibility?.eligibility || policyDetails?.roomEligibility || '[Room Eligibility]';

  const coPayPercent = coPayStructure?.percent ?? coPayStructure?.percentage ?? policyDetails?.coPayment;
  const coPayment = (coPayPercent !== undefined && coPayPercent !== null) ? `${coPayPercent}%` : '[Co-payment]';

  const waitingPeriod = policy.waitingPeriod || policyDetails.waitingPeriod || '[Waiting Period]';

  const applicationStatus = applicationData?.status || '[Status]';
  const approvedCoverage = formatCurrency(pv.approvedCoverageAmount) || '[Approved Coverage]';
  const effectiveDate = formatDate(pv?.verifiedAt) || '[Effective Date]';
  const roomRentLimit = roomEligibility?.limit || policyDetails?.roomRentLimit || '[Room Rent Limit]';
  const icuCoverage = policyDetails?.icuCoverage || '[ICU Coverage]';
  const prePostCoverage = policyDetails?.prePostCoverage || '[Pre/Post Coverage]';
  const finalCoPayment = coPayment;

  const templateData = {
    patientName: applicationData?.patient?.name || '[Patient Name]',
    reviewerName: applicationData?.reviewerName || '[Reviewer Name]',
    companyName: 'Samriddhi Healthcare',
    applicationId: applicationData?.applicationId || '[Application ID]',
    policyNumber: policyNumber || '[Policy Number]',
    insuranceCompany: insuranceCompany || '[Insurance Company]',
    sumInsured: sumInsured || '[Sum Insured]',
    policyPeriod,
    diagnosis,
    hospitalName,
    estimatedCost,
    admissionDate,
    availableCoverage: availableCoverage || '[Available Coverage]',
    roomEligibility: roomEligibilityText,
    coPayment,
    waitingPeriod,
    applicationStatus,
    approvedCoverage,
    effectiveDate,
    roomRentLimit,
    icuCoverage,
    prePostCoverage,
    finalCoPayment
  };

  const script = scripts[consultationType];
  if (!script) return null;

  // Replace placeholders in the script
  const processedScript = {
    ...script,
    introduction: replaceTemplate(script.introduction, templateData),
    sections: script.sections.map(section => ({
      ...section,
      points: section.points.map(point => replaceTemplate(point, templateData))
    })),
    conclusion: replaceTemplate(script.conclusion, templateData)
  };

  return processedScript;
};

// Get consultations for an application
export const getApplicationConsultations = async (applicationId) => {
  try {
    const q = query(
      collection(db, "policyConsultations"),
      where("applicationId", "==", applicationId),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const consultations = [];

    querySnapshot.forEach((doc) => {
      consultations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return {
      success: true,
      consultations
    };
  } catch (error) {
    console.error("Error getting consultations:", error);
    return {
      success: false,
      error: error.message,
      consultations: []
    };
  }
};

// Get pending consultations for policy writer
export const getPendingConsultations = async (policyWriterId) => {
  try {
    const q = query(
      collection(db, "policyConsultations"),
      where("policyWriterId", "==", policyWriterId),
      where("status", "in", ["scheduled", "in-progress"]),
      orderBy("scheduledDate", "asc")
    );

    const querySnapshot = await getDocs(q);
    const consultations = [];

    querySnapshot.forEach((doc) => {
      consultations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return {
      success: true,
      consultations
    };
  } catch (error) {
    console.error("Error getting pending consultations:", error);
    return {
      success: false,
      error: error.message,
      consultations: []
    };
  }
};

// Reschedule consultation
export const rescheduleConsultation = async (consultationId, newScheduleData) => {
  try {
    const consultationRef = doc(db, "policyConsultations", consultationId);
    
    await updateDoc(consultationRef, {
      scheduledDate: Timestamp.fromDate(new Date(newScheduleData.scheduledDate)),
      scheduledTime: newScheduleData.scheduledTime,
      rescheduleReason: newScheduleData.reason || '',
      rescheduledAt: Timestamp.now(),
      rescheduledBy: newScheduleData.rescheduledBy,
      reminderSent: false,
      updatedAt: Timestamp.now()
    });

    return {
      success: true,
      message: "Consultation rescheduled successfully"
    };
  } catch (error) {
    console.error("Error rescheduling consultation:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Cancel consultation
export const cancelConsultation = async (consultationId, cancellationData) => {
  try {
    const consultationRef = doc(db, "policyConsultations", consultationId);
    
    await updateDoc(consultationRef, {
      status: 'cancelled',
      cancellationReason: cancellationData.reason || '',
      cancelledAt: Timestamp.now(),
      cancelledBy: cancellationData.cancelledBy,
      updatedAt: Timestamp.now()
    });

    return {
      success: true,
      message: "Consultation cancelled successfully"
    };
  } catch (error) {
    console.error("Error cancelling consultation:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get consultation analytics
export const getConsultationAnalytics = async (policyWriterId, dateRange = null) => {
  try {
    let q = collection(db, "policyConsultations");
    q = query(q, where("policyWriterId", "==", policyWriterId));

    if (dateRange && dateRange.start && dateRange.end) {
      q = query(
        q,
        where("createdAt", ">=", Timestamp.fromDate(new Date(dateRange.start))),
        where("createdAt", "<=", Timestamp.fromDate(new Date(dateRange.end)))
      );
    }

    const querySnapshot = await getDocs(q);
    
    const analytics = {
      totalConsultations: 0,
      preConsultations: 0,
      postConsultations: 0,
      completedConsultations: 0,
      scheduledConsultations: 0,
      cancelledConsultations: 0,
      averageDuration: 0,
      totalDuration: 0,
      averageSatisfaction: 0,
      followUpRequired: 0
    };

    let totalDuration = 0;
    let satisfactionSum = 0;
    let satisfactionCount = 0;

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      analytics.totalConsultations++;

      if (data.consultationType === 'pre-consultation') {
        analytics.preConsultations++;
      } else {
        analytics.postConsultations++;
      }

      switch (data.status) {
        case 'completed':
          analytics.completedConsultations++;
          break;
        case 'scheduled':
          analytics.scheduledConsultations++;
          break;
        case 'cancelled':
          analytics.cancelledConsultations++;
          break;
      }

      if (data.duration) {
        totalDuration += data.duration;
      }

      if (data.patientSatisfaction) {
        satisfactionSum += data.patientSatisfaction;
        satisfactionCount++;
      }

      if (data.followUpRequired) {
        analytics.followUpRequired++;
      }
    });

    analytics.averageDuration = analytics.completedConsultations > 0
      ? Math.round(totalDuration / analytics.completedConsultations)
      : 0;

    analytics.totalDuration = totalDuration;

    analytics.averageSatisfaction = satisfactionCount > 0
      ? Math.round((satisfactionSum / satisfactionCount) * 10) / 10
      : 0;

    return {
      success: true,
      analytics
    };
  } catch (error) {
    console.error("Error getting consultation analytics:", error);
    return {
      success: false,
      error: error.message,
      analytics: null
    };
  }
};

// Get applications with pending counselling for easy navigation
export const getApplicationsWithPendingCounselling = async (filters = {}, page = 1, pageSize = 10) => {
  try {
    console.log('policyWriterService: getApplicationsWithPendingCounselling called with filters:', filters);
    
    let q = collection(db, "patientApplications");

    // Fetch applications (broad), then strictly gate by workflow below
    const querySnapshot = await getDocs(q);
    let applications = [];

    console.log('policyWriterService: Total documents found:', querySnapshot.size);

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const w = data.workflow || {};
      const pv = w.policyVerification || {};
      const mv = w.medicalVerification || {};
      const ds = w.dischargeSettlement || {};
      const dispatchObj = w.dispatch || w.dispatches || {};

      // Gating signals
      const kypAck = mv?.kypHospitalAcknowledgement?.status || mv?.kypAck || mv?.kypStatus;
      const kypAccepted = kypAck === 'accepted';

      const dispatchApproved = (
        w?.dispatchClearance?.status === 'approved' ||
        dispatchObj?.status === 'completed' ||
        ds?.dispatchClearance?.status === 'approved' ||
        ds?.dispatchReview?.status === 'approved'
      );

      // Current session states
      const kic = pv?.kypInitialCounselling || {};
      const dsc = pv?.dischargeCounselling || {};

      const kypCounsellingPending = kypAccepted && (!kic?.status || ['pending','scheduled','in-progress'].includes((kic.status || '').toLowerCase()));
      const dischargeCounsellingPending = dispatchApproved && (!dsc?.status || ['pending','scheduled','in-progress'].includes((dsc.status || '').toLowerCase()));

      // Include only if at least one session is genuinely pending per gating rules
      if (kypCounsellingPending || dischargeCounsellingPending) {
        applications.push({
          id: doc.id,
          ...data,
          counsellingNeeded: {
            kypInitial: kypCounsellingPending,
            discharge: dischargeCounsellingPending
          },
          pendingCounselling: [
            ...(kypCounsellingPending ? ['kyp-initial-counselling'] : []),
            ...(dischargeCounsellingPending ? ['discharge-counselling'] : [])
          ]
        });
      }
    });

    console.log('policyWriterService: Applications after status filtering:', applications.length);

    // Apply filters (already gated). counsellingType: 'kyp-initial' | 'discharge'
    if (filters.counsellingType) {
      if (filters.counsellingType === 'kyp-initial') {
        applications = applications.filter(app => app.counsellingNeeded?.kypInitial);
      } else if (filters.counsellingType === 'discharge') {
        applications = applications.filter(app => app.counsellingNeeded?.discharge);
      }
    }

    // Apply other filters
    if (filters.priority && filters.priority !== "") {
      applications = applications.filter(app => app.priority === filters.priority);
    }

    if (filters.emergency !== "" && filters.emergency !== undefined) {
      const isEmergency = filters.emergency === "true";
      applications = applications.filter(app => app.emergency === isEmergency);
    }

    if (filters.insuranceCompany && filters.insuranceCompany !== "") {
      applications = applications.filter(app => 
        app.policy?.insuranceCompany === filters.insuranceCompany
      );
    }

    if (filters.searchTerm && filters.searchTerm.trim() !== "") {
      const searchTerm = filters.searchTerm.toLowerCase();
      applications = applications.filter(app =>
        app.applicationId?.toLowerCase().includes(searchTerm) ||
        app.patient?.name?.toLowerCase().includes(searchTerm) ||
        app.policy?.number?.toLowerCase().includes(searchTerm) ||
        app.hospitalName?.toLowerCase().includes(searchTerm)
      );
    }

    // Sort by emergency first, then by submission date
    applications.sort((a, b) => {
      if (a.emergency && !b.emergency) return -1;
      if (!a.emergency && b.emergency) return 1;
      
      const aDate = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt);
      const bDate = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt);
      return bDate - aDate;
    });

    // Pagination
    const total = applications.length;
    const startIndex = (page - 1) * pageSize;
    const paginatedApplications = applications.slice(startIndex, startIndex + pageSize);

    const result = {
      success: true,
      applications: paginatedApplications,
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize),
      counsellingCounts: {
        kypInitial: applications.filter(app => app.counsellingNeeded?.kypInitial).length,
        discharge: applications.filter(app => app.counsellingNeeded?.discharge).length,
        total: applications.length
      }
    };

    console.log('Final result:', {
      total: result.total,
      applicationsCount: result.applications.length,
      counsellingCounts: result.counsellingCounts
    });

    return result;
  } catch (error) {
    console.error("Error getting applications with pending counselling:", error);
    return {
      success: false,
      error: error.message,
      applications: [],
      total: 0,
      counsellingCounts: { kypInitial: 0, discharge: 0, total: 0 }
    };
  }
};

// Check if application has unresolved queries that would block policy review
// options.department: filter queries by department (e.g., 'policy', 'medical', 'financial')
export const checkApplicationQueryStatus = async (applicationId, options = {}) => {
  try {
    // Import checkApplicationDisputeStatus from disputeService
    const { checkApplicationDisputeStatus } = await import('./disputeService');
    
    const result = await checkApplicationDisputeStatus(applicationId, options);
    
    if (!result.success) {
      return {
        success: false,
        error: result.error,
        hasUnresolvedQueries: false,
        canProceed: false
      };
    }
    
    return {
      success: true,
      hasUnresolvedQueries: result.hasActiveDisputes,
      unresolvedQueries: result.activeDisputes,
      canProceed: result.canProceed,
      queryCount: result.activeDisputes?.length || 0
    };
  } catch (error) {
    console.error("Error checking application query status:", error);
    return {
      success: false,
      error: error.message,
      hasUnresolvedQueries: false,
      canProceed: false
    };
  }
};

// Get applications that are on hold due to unresolved queries (for policy team)
export const getPolicyApplicationsOnHold = async (
  filters = {},
  page = 1,
  pageSize = 10
) => {
  try {
    // Get applications with dispute_pending status that would normally be in policy review
    let q = query(
      collection(db, "patientApplications"),
      where("status", "==", "dispute_pending"),
      orderBy("updatedAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    let applications = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Only include applications that would be in policy phase
      if (data.workflow?.policyVerification?.status === "pending" || 
          data.status === "medical_approved" || 
          data.status === "policy_review_pending" ||
          data.status === "approved_for_process") {
        applications.push({
          id: doc.id,
          ...data,
        });
      }
    });

    // For each application, get the active queries/disputes
    for (let app of applications) {
      try {
        const queryStatus = await checkApplicationQueryStatus(app.applicationId);
        app.queryStatus = queryStatus;
      } catch (error) {
        console.error(`Error getting query status for ${app.applicationId}:`, error);
        app.queryStatus = {
          hasUnresolvedQueries: false,
          unresolvedQueries: [],
          canProceed: false,
          queryCount: 0
        };
      }
    }

    // Apply client-side filtering
    if (filters.searchTerm && filters.searchTerm.trim() !== "") {
      const searchTerm = filters.searchTerm.toLowerCase();
      applications = applications.filter(
        (app) =>
          app.applicationId?.toLowerCase().includes(searchTerm) ||
          app.patient?.name?.toLowerCase().includes(searchTerm) ||
          app.medical?.diagnosis?.toLowerCase().includes(searchTerm) ||
          app.hospitalName?.toLowerCase().includes(searchTerm)
      );
    }

    // Pagination
    const total = applications.length;
    const startIndex = (page - 1) * pageSize;
    const paginatedApplications = applications.slice(
      startIndex,
      startIndex + pageSize
    );

    return {
      success: true,
      applications: paginatedApplications,
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize),
    };
  } catch (error) {
    console.error("Error getting policy applications on hold:", error);
    return {
      success: false,
      error: error.message,
      applications: [],
      total: 0,
    };
  }
};

// Enhanced function to check if application can proceed to policy review
// For policy team: only check queries raised BY policy team
export const canApplicationProceedToPolicy = async (applicationId) => {
  try {
    // Check for unresolved queries raised by policy team only
    const queryStatus = await checkApplicationQueryStatus(applicationId, { department: 'policy' });
    
    if (!queryStatus.success) {
      return {
        success: false,
        canProceed: false,
        reason: "Error checking query status",
        error: queryStatus.error
      };
    }

    if (queryStatus.hasUnresolvedQueries) {
      return {
        success: true,
        canProceed: false,
        reason: "Application is on hold due to unresolved queries raised by policy team",
        unresolvedQueries: queryStatus.unresolvedQueries,
        queryCount: queryStatus.queryCount,
        phase: "policy"
      };
    }

    return {
      success: true,
      canProceed: true,
      reason: "No blocking queries found from policy team",
      phase: "policy"
    };
  } catch (error) {
    console.error("Error checking if application can proceed to policy review:", error);
    return {
      success: false,
      canProceed: false,
      reason: "Error during validation",
      error: error.message
    };
  }
};